import { useEffect, useState } from 'react';
import { channelLabel, type QuotationResource } from '@neuroedge/shared';
import { Button, Modal, Select, useToast } from '../../components/ui';
import { useChannels } from '../../api/channels';
import { useSendQuotation } from '../../api/quotations';
import { formatMoney } from './quotation-format';

/** Send (or re-send) a quotation's PDF to its contact over WhatsApp. */
export function SendQuotationModal({
  quotation,
  onClose,
}: {
  quotation: QuotationResource;
  onClose: () => void;
}) {
  const toast = useToast();
  const send = useSendQuotation();
  const channels = useChannels({ type: 'WHATSAPP' });
  const connected = (channels.data ?? []).filter((c) => c.status === 'CONNECTED');
  const [channelId, setChannelId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (connected.length === 1 && !channelId) setChannelId(connected[0]!.id);
  }, [connected, channelId]);

  async function onSend() {
    setError(null);
    if (!channelId) {
      setError('Choose the WhatsApp number to send from.');
      return;
    }
    try {
      const result = await send.mutateAsync({ quotationId: quotation.id, input: { channelId } });
      if (result.status === 'FAILED') {
        const message = result.errorMessage ?? 'The quotation could not be sent.';
        setError(message);
        toast.error(message);
        return;
      }
      toast.success('Quotation sent.');
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not send the quotation.';
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Modal title={`Send quotation ${quotation.reference}`} onClose={onClose}>
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <p style={{ margin: 0 }}>
          Send the PDF ({formatMoney(quotation.subtotalAmount, quotation.currency)}) to{' '}
          <strong>{quotation.contactDisplayName ?? 'this contact'}</strong>
          {quotation.contactPhone ? ` (${quotation.contactPhone})` : ''} over WhatsApp.
        </p>
        <Select
          label="Send from"
          value={channelId}
          onChange={(e) => {
            setChannelId(e.target.value);
            setError(null);
          }}
          options={[
            { value: '', label: channels.isPending ? 'Loading…' : 'Choose a number…' },
            ...connected.map((c) => ({ value: c.id, label: channelLabel(c) })),
          ]}
        />
        {error ? <p style={{ margin: 0, color: 'var(--color-danger)' }}>{error}</p> : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="secondary" onClick={onClose} disabled={send.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void onSend()} disabled={send.isPending}>
            {send.isPending ? 'Sending…' : 'Send over WhatsApp'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
